import React, { useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'
import { Link, useSearchParams } from 'react-router-dom'
import Panel from '../../components/Panel'
import Badge from '../../components/Badge'
import Paginator from '../../components/Table/Paginator'
import QueryError from './QueryError'
import ConductorFieldKey from './ConductorFieldKey'
import { TH } from './conductorTableCells'
import {
  formatConductorDate,
  formatConductorTimestamp,
  isWorkItemOverdue,
  paginatedListMode,
  shouldShowPaginator,
} from './conductorUtils'
import { useClampedPage } from './useClampedPage'
import { useAuth0 } from '@auth0/auth0-react'
import WorkItemAssignmentActions from './WorkItemAssignmentActions'
import { useGetConductorPortfolioQuery, useGetConductorWorkItemsQuery } from './conductorApi'

import type { ConductorWorkItem } from './conductor.types'

const INBOX_PAGE_SIZE = 100

const STATUS_OPTIONS = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CANCELLED', 'EXPIRED'] as const
const TYPE_OPTIONS = ['TASK', 'APPROVAL', 'ISSUE', 'HOLD'] as const

/** UI-only filter values; `ACTIVE` maps to API `activeOnly`. */
type StatusFilter = 'ACTIVE' | 'ALL' | (typeof STATUS_OPTIONS)[number]

const selectClasses =
  'rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100'

const ItemRow: React.FC<{ item: ConductorWorkItem, currentUserEmail: string }> = ({ item, currentUserEmail }) => {
  const [expanded, setExpanded] = useState(false)
  const overdue = isWorkItemOverdue(item)
  const hasDescription = item.description != null && item.description !== ''

  return (
    <>
      <tr className="border-t border-slate-100 dark:border-slate-700">
        <td className="px-3 py-2">
          <button
            type="button"
            aria-expanded={expanded}
            className="text-left text-sm font-medium text-slate-900 dark:text-slate-100"
            onClick={() => setExpanded((v) => !v)}
          >
            <span aria-hidden="true">{expanded ? '▾' : '▸'}</span> <span>{item.title}</span>
          </button>
          <div className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
            <Link
              to={`/conductor/instances/${item.workflowInstanceCuid}`}
              onClick={(e) => e.stopPropagation()}
              className="font-mono"
            >
              {item.workflowInstanceCuid}
            </Link>
            {item.stepKey != null && <span className="ml-2">step {item.stepKey}</span>}
          </div>
        </td>
        <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{item.ownerRole}</td>
        <td className="px-3 py-2">
          <Badge status={item.type} />
        </td>
        <td className="px-3 py-2">
          <Badge status={item.status} />
        </td>
        <td className="px-3 py-2 text-sm">
          {item.dueAt == null ? (
            <span className="text-slate-400 dark:text-slate-500">—</span>
          ) : (
            <span
              className={classnames(
                overdue ? 'font-semibold text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-200'
              )}
            >
              {formatConductorDate(item.dueAt)}
              {overdue && ' (overdue)'}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-right text-sm text-slate-500 dark:text-slate-400">
          {formatConductorTimestamp(item.createdAt)}
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-slate-100 dark:border-slate-700">
          <td colSpan={6} className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
            <div className="space-y-1">
              {hasDescription && <div>{item.description}</div>}
              {item.stepKey != null && <div>Step {item.stepKey}</div>}
              <div>Assignee {item.assigneeUserId ?? '—'}</div>
              <div>Created {formatConductorTimestamp(item.createdAt)}</div>
              <div>
                Resolved {item.resolvedAt != null ? formatConductorTimestamp(item.resolvedAt) : '—'}
                {item.resolvedBy != null && ` by ${item.resolvedBy}`}
              </div>
              {item.resolution != null && item.resolution !== '' && <div>{item.resolution}</div>}
              <WorkItemAssignmentActions item={item} currentUserEmail={currentUserEmail} />
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export const Inbox: React.FC = () => {
  const { user } = useAuth0()
  const currentUserEmail = user?.email?.trim() ?? ''
  const [searchParams, setSearchParams] = useSearchParams()
  const rawSubjectCuid = searchParams.get('subjectCuid')
  const subjectCuid = (rawSubjectCuid ?? '').trim()
  const [role, setRole] = useState<string>('')
  const [status, setStatus] = useState<StatusFilter>('ACTIVE')
  const [type, setType] = useState<string>('')
  const [page, setPage] = useState(0)
  const [appliedSubjectCuid, setAppliedSubjectCuid] = useState(subjectCuid)
  const subjectChanged = appliedSubjectCuid !== subjectCuid
  if (subjectChanged) {
    setAppliedSubjectCuid(subjectCuid)
    setPage(0)
  }
  const queryPage = subjectChanged ? 0 : page

  const { data: portfolio } = useGetConductorPortfolioQuery()

  const setRoleFilter = (value: string): void => {
    setRole(value)
    setPage(0)
  }

  const setStatusFilter = (value: StatusFilter): void => {
    setStatus(value)
    setPage(0)
  }

  const setTypeFilter = (value: string): void => {
    setType(value)
    setPage(0)
  }

  useEffect(() => {
    if (rawSubjectCuid != null && rawSubjectCuid.trim() === '') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('subjectCuid')
          return next
        },
        { replace: true }
      )
    }
  }, [rawSubjectCuid, setSearchParams])

  const clearSubjectFilter = (): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('subjectCuid')
      return next
    })
    setPage(0)
  }

  const itemsQuery = useGetConductorWorkItemsQuery({
    ownerRole: role === '' ? undefined : role,
    activeOnly: status === 'ACTIVE' ? true : undefined,
    status: status === 'ACTIVE' || status === 'ALL' ? undefined : status,
    type: type === '' ? undefined : type,
    subjectCuid: subjectCuid === '' ? undefined : subjectCuid,
    resultsPerPage: INBOX_PAGE_SIZE,
    page: queryPage + 1,
  })

  const itemsData = itemsQuery.data
  useClampedPage(itemsData?.total, itemsData?.resultsPerPage, page, setPage)

  const roles = useMemo(() => {
    const fromPortfolio = portfolio?.openWorkItemsByRole.map((r) => r.ownerRole) ?? []
    const fromResults = itemsData?.results.map((item) => item.ownerRole) ?? []
    return [...new Set([...fromPortfolio, ...fromResults])].sort((a, b) => a.localeCompare(b))
  }, [portfolio, itemsData?.results])

  const listMode =
    itemsData == null
      ? null
      : paginatedListMode({
          total: itemsData.total,
          results: itemsData.results,
          resultsPerPage: itemsData.resultsPerPage,
        })

  return (
    <div className="space-y-4 p-4">
      <div>
        <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">Work Inbox</h2>
        <p className="m-0 text-xs text-slate-500 dark:text-slate-400">
          Role-assigned work items from playbook steps, ordered by due date — items resolve automatically when their
          gate passes
        </p>
        <ConductorFieldKey screen="inbox" />
      </div>

      <Panel header="Work items">
        {subjectCuid !== '' ? (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            <span>
              Filtered to subject <span className="font-mono">{subjectCuid}</span>
            </span>
            <button
              type="button"
              className="text-indigo-600 hover:underline dark:text-indigo-400"
              onClick={clearSubjectFilter}
            >
              Clear subject filter
            </button>
          </div>
        ) : null}
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Role
            <select aria-label="Filter by role" value={role} onChange={(e) => setRoleFilter(e.target.value)} className={selectClasses}>
              <option value="">All roles</option>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Status
            <select
              aria-label="Filter by status"
              value={status}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className={selectClasses}
            >
              <option value="ACTIVE">Open &amp; in progress</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
              <option value="ALL">All statuses</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Type
            <select aria-label="Filter by type" value={type} onChange={(e) => setTypeFilter(e.target.value)} className={selectClasses}>
              <option value="">All types</option>
              {TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        </div>

        {itemsQuery.isError ? (
          <QueryError message="Failed to load work items." onRetry={() => void itemsQuery.refetch()} />
        ) : itemsData == null ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
        ) : listMode === 'empty' ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">
            No work items match the current filters.
          </div>
        ) : (
          <>
            {listMode === 'page-pending' && (
              <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading page…</div>
            )}
            {listMode === 'content' && (
              <table className="w-full">
                <thead>
                  <tr>
                    <TH>Item</TH>
                    <TH>Role</TH>
                    <TH>Type</TH>
                    <TH>Status</TH>
                    <TH>Due</TH>
                    <TH align="right">Created</TH>
                  </tr>
                </thead>
                <tbody>
                  {itemsData.results.map((item) => (
                    <ItemRow key={item.cuid} item={item} currentUserEmail={currentUserEmail} />
                  ))}
                </tbody>
              </table>
            )}
            {shouldShowPaginator(itemsData.total, itemsData.resultsPerPage) && (
              <Paginator
                className="-mx-4 mt-2 border-t border-slate-200 dark:border-slate-700"
                data={{
                  total: itemsData.total,
                  resultsPerPage: itemsData.resultsPerPage,
                  page,
                }}
                handlePaginatorClick={setPage}
              />
            )}
          </>
        )}
      </Panel>
    </div>
  )
}

export default Inbox
